/**
 * Keyboard Shortcuts Hook
 * Centralized keyboard shortcut handling
 */

import { useEffect, useCallback } from 'react'

export interface ShortcutConfig {
  key: string
  ctrl?: boolean
  shift?: boolean
  alt?: boolean
  action: () => void
  description?: string
}

/**
 * Hook for registering keyboard shortcuts
 * @param shortcuts Array of shortcut configurations
 * @param enabled Whether shortcuts are enabled (default: true)
 */
export function useKeyboardShortcuts(shortcuts: ShortcutConfig[], enabled: boolean = true) {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) return

      // Don't trigger shortcuts when typing in input fields
      const target = event.target as HTMLElement
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return
      }

      for (const shortcut of shortcuts) {
        const keyMatches = event.key.toLowerCase() === shortcut.key.toLowerCase()
        const ctrlMatches = shortcut.ctrl ? event.ctrlKey || event.metaKey : !event.ctrlKey && !event.metaKey
        const shiftMatches = shortcut.shift ? event.shiftKey : !event.shiftKey
        const altMatches = shortcut.alt ? event.altKey : !event.altKey

        if (keyMatches && ctrlMatches && shiftMatches && altMatches) {
          event.preventDefault()
          event.stopPropagation()
          shortcut.action()
          return
        }
      }
    },
    [shortcuts, enabled]
  )

  useEffect(() => {
    if (!enabled) return

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown, enabled])
}

/**
 * Format shortcut for display (e.g., "Ctrl+S")
 */
export function formatShortcut(config: ShortcutConfig): string {
  const parts: string[] = []

  if (config.ctrl) parts.push('Ctrl')
  if (config.shift) parts.push('Shift')
  if (config.alt) parts.push('Alt')
  parts.push(config.key.toUpperCase())

  return parts.join('+')
}

/**
 * Common shortcut configurations
 */
export const CommonShortcuts = {
  // File operations
  NEW: { key: 'n', ctrl: true, description: 'New Diagram' },
  OPEN: { key: 'o', ctrl: true, description: 'Open JSON' },
  SAVE: { key: 's', ctrl: true, description: 'Save JSON' },

  // Edit operations
  UNDO: { key: 'z', ctrl: true, description: 'Undo' },
  REDO: { key: 'y', ctrl: true, description: 'Redo' },
  CUT: { key: 'x', ctrl: true, description: 'Cut' },
  COPY: { key: 'c', ctrl: true, description: 'Copy' },
  PASTE: { key: 'v', ctrl: true, description: 'Paste' },
  SELECT_ALL: { key: 'a', ctrl: true, description: 'Select All' },
  DELETE: { key: 'Delete', description: 'Delete Selected' },

  // View operations
  TOGGLE_NODE_LIBRARY: { key: 'l', ctrl: true, description: 'Toggle Node Library' },
  TOGGLE_PROPERTIES: { key: 'p', ctrl: true, description: 'Toggle Properties Panel' },
  TOGGLE_GRID: { key: 'g', ctrl: true, description: 'Toggle Grid' },
  FIT_VIEW: { key: '0', ctrl: true, description: 'Fit View' },
  ZOOM_IN: { key: '+', ctrl: true, description: 'Zoom In' },
  ZOOM_OUT: { key: '-', ctrl: true, description: 'Zoom Out' },

  // Help
  HELP: { key: 'F1', description: 'Help' },
  SHORTCUTS: { key: '/', ctrl: true, description: 'Keyboard Shortcuts' },
} as const
